import * as ExcelJS from "exceljs";
import { Localization } from "@viamap/viamap2-common";
import { AttributeValue, getPLAHierarchicalRelations, getPLAValueTypePostfix, isPLANumberValueType, PLA_CUBE_KEY_SEPARATOR, PLA_CUBE_TOTAL_KEY, PLAElementRecord, PLAProject, PLARelationRecord, PLAValueType } from "./PLAState";
import { getPLAAlertDetails, resolvePLACalculatedAttributes } from "./PLAExpression";
import { comparePLAElementsByDefaultOrder } from "./PLAElementSorting";

type ReportRow = {
   kind: "data" | "subtotal" | "grandTotal";
   level: number;
   structure: string;
   relation: string;
   elementType: string;
   name: string;
   attributes: Record<string, any>;
   attributeValueTypes: Record<string, PLAValueType | undefined>;
   warnings: Record<string, string>;
};

type AttributeReportColumn = { header: string; attributeKey: string; valueType?: PLAValueType };
type GroupedReportColumn = { header: string; key: string; width: number; warningKey?: string; group?: string };
type GroupedReportRow = {
   kind: "data" | "subtotal" | "grandTotal";
   level: number;
   values: Record<string, any>;
   warnings: Record<string, string>;
   sortElement?: PLAElementRecord;
};
type ArealRefGroup = { key: string; label: string; sortElement?: PLAElementRecord };
type FreeAreaReportColumn = { header: string; key: string; width: number; group?: string; type?: "text" | "number" | "percent" | "separator" };
type FreeAreaReportDataRow = {
   sortElement: PLAElementRecord;
   byggefeltName: string;
   freeTerrainArea: number;
   applicationAreas: Record<string, number>;
   applicationRequirements: Record<string, number>;
   totalApplicationArea: number;
   totalRequirement: number;
};
type FreeAreaReportGroup = { name: string; rows: FreeAreaReportDataRow[]; sortElement?: PLAElementRecord };
type FreeAreaApplicationGroup = { key: string; label: string; values: string[]; areaKey: string; secondaryKey?: string; percentKey: string; requirementKey: string };
type ReportReference = { ids: string[]; names?: string[] };
type FreeAreaReportReferences = {
   elementTypes: { delomraade: ReportReference; byggefelt: ReportReference; anvendelse: ReportReference };
   attributes: { anvendelse: ReportReference; etageareal: ReportReference; friarealskrav: ReportReference; friareal: ReportReference; anvendelseKorrigeret: ReportReference };
};

const reportFonts = {
   meta: { name: "Arial Narrow", size: 8 },
   title: { name: "Arial Narrow", size: 14, bold: true },
   section: { name: "Arial Narrow", size: 10, bold: true },
   header: { name: "Arial Narrow", size: 9, bold: true },
   body: { name: "Arial Narrow", size: 9 },
   total: { name: "Arial Narrow", size: 9, bold: true }
} as const;

const thinBorder = {
   top: { style: "thin" },
   left: { style: "thin" },
   bottom: { style: "thin" },
   right: { style: "thin" }
} as const;

function formatValue(value: any): string | number | boolean {
   if (value === undefined || value === null) return "";
   if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
   return JSON.stringify(value);
}

function getConstantValue(attribute: AttributeValue | undefined): any {
   if (!attribute) return "";
   if (attribute.mode === "constant") return attribute.value;
   return attribute.expression || "";
}

function safeFileName(name: string): string {
   return name.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim() || "PLA-report";
}

function collectElementDetails(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): { attributes: Record<string, any>; attributeValueTypes: Record<string, PLAValueType | undefined>; warnings: Record<string, string> } {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const calculated = resolvePLACalculatedAttributes(element, project, elements, relations, { includeErrors: true });
   const attributeLabels: Record<string, string> = {};
   const attributeValueTypes: Record<string, PLAValueType | undefined> = {};
   const attributes = Array.from(new Set([...Object.keys(definitions), ...Object.keys(element.attributes || {})]))
      .filter((attributeId) => attributeId.toLowerCase() !== "id")
      .reduce<Record<string, any>>((acc, attributeId) => {
         const definition = definitions[attributeId];
         const label = definition?.name || attributeId;
         attributeLabels[attributeId] = label;
         attributeValueTypes[label] = definition?.valueType;
         acc[label] = formatValue(definition?.type === "expression" ? calculated[attributeId] : getConstantValue(element.attributes[attributeId]));
         return acc;
      }, {});
   const warnings = getPLAAlertDetails(element, project, elements, relations).reduce<Record<string, string>>((acc, alert) => {
      const label = attributeLabels[alert.attributeId] || definitions[alert.attributeId]?.name || alert.attributeId;
      acc[label] = acc[label] ? `${acc[label]}\n${alert.message}` : alert.message;
      return acc;
   }, {});
   return { attributes, attributeValueTypes, warnings };
}

function getNumericTotals(attributes: Record<string, any>): Record<string, number> {
   return Object.entries(attributes).reduce<Record<string, number>>((acc, [key, value]) => {
      if (typeof value === "number" && Number.isFinite(value)) acc[key] = value;
      return acc;
   }, {});
}

function getExcelNumberFormat(valueType: PLAValueType | undefined) {
   return isPLANumberValueType(valueType) ? `#,##0.####"${getPLAValueTypePostfix(valueType)}"` : undefined;
}

function addTotals(target: Record<string, number>, source: Record<string, number>) {
   Object.entries(source).forEach(([key, value]) => {
      target[key] = (target[key] || 0) + value;
   });
}

function elementTypeName(project: PLAProject, element: PLAElementRecord) {
   return project.elementTypes[element.elementType]?.name || element.elementType;
}

function matchesElementType(project: PLAProject, element: PLAElementRecord, text: string) {
   const search = text.toLowerCase();
   return element.elementType.toLowerCase().includes(search) || elementTypeName(project, element).toLowerCase().includes(search);
}

function compareReportText(left: string, right: string) {
   return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
}

function hasPLAElementSortingNumber(element: PLAElementRecord | undefined) {
   return typeof element?.sortingNumber === "number" && Number.isFinite(element.sortingNumber);
}

function comparePLAElementSortingNumber(leftElement: PLAElementRecord | undefined, rightElement: PLAElementRecord | undefined) {
   const leftHasSortingNumber = hasPLAElementSortingNumber(leftElement);
   const rightHasSortingNumber = hasPLAElementSortingNumber(rightElement);
   if (leftHasSortingNumber && rightHasSortingNumber && leftElement!.sortingNumber !== rightElement!.sortingNumber) return leftElement!.sortingNumber! - rightElement!.sortingNumber!;
   if (leftHasSortingNumber && !rightHasSortingNumber) return -1;
   if (!leftHasSortingNumber && rightHasSortingNumber) return 1;
   return 0;
}

function comparePLAElementNames(leftElement: PLAElementRecord | undefined, rightElement: PLAElementRecord | undefined) {
   return compareReportText(leftElement?.name || "", rightElement?.name || "");
}

function comparePLAReportElements(leftElement: PLAElementRecord, rightElement: PLAElementRecord) {
   return comparePLAElementsByDefaultOrder(leftElement, rightElement);
}

function comparePLAReportRelations(leftRelation: PLARelationRecord, rightRelation: PLARelationRecord, elementsById: Map<string, PLAElementRecord>, relationLabel: (relation: PLARelationRecord) => string) {
   const leftElement = elementsById.get(leftRelation.toId);
   const rightElement = elementsById.get(rightRelation.toId);
   return comparePLAElementSortingNumber(leftElement, rightElement) || compareReportText(relationLabel(leftRelation), relationLabel(rightRelation)) || comparePLAElementNames(leftElement, rightElement);
}

const groupedReportFixedAttributes = ["Grundareal", "Friareal", "ByggeretOprindelig", "ByggeretReduceret"];
const groupedReportAnvendelseAttributes = ["Etageareal", "Friarealkrav"];
const defaultFreeAreaReportReferences: FreeAreaReportReferences = {
   elementTypes: {
      delomraade: { ids: ["delomraade", "delområde", "bydel"], names: ["Delområde", "Delomraade", "Bydel"] },
      byggefelt: { ids: ["byggefelt"], names: ["Byggefelt"] },
      anvendelse: { ids: ["anvendelse"], names: ["Anvendelse"] }
   },
   attributes: {
      anvendelse: { ids: ["anvendelse"], names: ["Anvendelse"] },
      etageareal: { ids: ["etageAreal", "etageareal", "udnyttedeByggeretter"], names: ["Udnyttede byggeretter", "Etageareal"] },
      friarealskrav: { ids: ["friarealskrav", "friarealKrav"], names: ["Friarealskrav", "Friarealkrav"] },
      friareal: { ids: ["friareal", "friarealA"], names: ["Friareal (A)", "FriarealA", "Friareal"] },
      anvendelseKorrigeret: { ids: ["anvendelseKorrigeret"], names: ["AnvendelseKorrigeret"] }
   }
};
const freeAreaHeaderFill = "FF879B8D";
const freeAreaSubtotalFill = "FFA6A6A6";
const freeAreaAlternateRowFill = "FFCFD7D2";
const freeAreaApplicationGroups: FreeAreaApplicationGroup[] = [
   { key: "bolig", label: "Boliger inkl. Almen", values: ["Bolig"], areaKey: "boligArea", secondaryKey: "boligAlmen", percentKey: "boligPercent", requirementKey: "boligRequirement" },
   { key: "erhverv", label: "Erhverv", values: ["Erhverv"], areaKey: "erhvervArea", secondaryKey: "serviceErhverv", percentKey: "erhvervPercent", requirementKey: "erhvervRequirement" },
   { key: "borneinst", label: "Børneinstitutioner", values: ["Børneinst", "BørneInst"], areaKey: "borneinstArea", percentKey: "borneinstPercent", requirementKey: "borneinstRequirement" },
   { key: "grundskole", label: "Grundskoler", values: ["Grundskole", "Grundskoler"], areaKey: "grundskoleArea", percentKey: "grundskolePercent", requirementKey: "grundskoleRequirement" },
   { key: "pleje", label: "Plejecentre og plejeboliger", values: ["Pleje", "Plejecentre og plejeboliger"], areaKey: "plejeArea", percentKey: "plejePercent", requirementKey: "plejeRequirement" },
   { key: "kollegieung", label: "Kollegie- og Ungdomsboliger", values: ["KollegieUng", "Kollegieung", "Kollegie- og Ungdomsboliger"], areaKey: "kollegieUngArea", percentKey: "kollegieUngPercent", requirementKey: "kollegieUngRequirement" }
];
const freeAreaReportColumns: FreeAreaReportColumn[] = [
   { header: "Byggefelt", key: "byggefelt", width: 22, type: "text" },
   { header: "Byggefeltstatus", key: "byggefeltStatus", width: 16, type: "text" },
   { header: "Friareal i terræn", key: "freeTerrainArea", width: 16, type: "number" },
   { header: "Friareal over terræn", key: "freeRoofArea", width: 18, type: "number" },
   { header: "Friareal i alt", key: "freeAreaTotal", width: 16, type: "number" },
   { header: "", key: "separator1", width: 3, type: "separator" },
   { header: "Etageareal", key: "boligArea", width: 14, group: "Boliger inkl. Almen", type: "number" },
   { header: "Heraf Almen", key: "boligAlmen", width: 14, group: "Boliger inkl. Almen", type: "number" },
   { header: "Andel i %", key: "boligPercent", width: 12, group: "Boliger inkl. Almen", type: "percent" },
   { header: "Friarealskrav jf. Lokalplan", key: "boligRequirement", width: 18, group: "Boliger inkl. Almen", type: "number" },
   { header: "", key: "separator2", width: 3, type: "separator" },
   { header: "Etageareal", key: "erhvervArea", width: 14, group: "Erhverv", type: "number" },
   { header: "Heraf serviceerhverv", key: "serviceErhverv", width: 18, group: "Erhverv", type: "number" },
   { header: "Andel i %", key: "erhvervPercent", width: 12, group: "Erhverv", type: "percent" },
   { header: "Friarealskrav jf. Lokalplan", key: "erhvervRequirement", width: 18, group: "Erhverv", type: "number" },
   { header: "", key: "separator3", width: 3, type: "separator" },
   { header: "Etageareal", key: "borneinstArea", width: 14, group: "Børneinstitutioner", type: "number" },
   { header: "Andel i %", key: "borneinstPercent", width: 12, group: "Børneinstitutioner", type: "percent" },
   { header: "Friarealskrav jf. Lokalplan", key: "borneinstRequirement", width: 18, group: "Børneinstitutioner", type: "number" },
   { header: "", key: "separator4", width: 3, type: "separator" },
   { header: "Etageareal", key: "grundskoleArea", width: 14, group: "Grundskoler", type: "number" },
   { header: "Andel i %", key: "grundskolePercent", width: 12, group: "Grundskoler", type: "percent" },
   { header: "Friarealskrav jf. Lokalplan", key: "grundskoleRequirement", width: 18, group: "Grundskoler", type: "number" },
   { header: "", key: "separator5", width: 3, type: "separator" },
   { header: "Etageareal", key: "plejeArea", width: 14, group: "Plejecentre og plejeboliger", type: "number" },
   { header: "Andel i %", key: "plejePercent", width: 12, group: "Plejecentre og plejeboliger", type: "percent" },
   { header: "Friarealskrav jf. Lokalplan", key: "plejeRequirement", width: 18, group: "Plejecentre og plejeboliger", type: "number" },
   { header: "", key: "separator6", width: 3, type: "separator" },
   { header: "Etageareal", key: "kollegieUngArea", width: 14, group: "Kollegie- og Ungdomsboliger", type: "number" },
   { header: "Andel i %", key: "kollegieUngPercent", width: 12, group: "Kollegie- og Ungdomsboliger", type: "percent" },
   { header: "Friarealskrav jf. Lokalplan", key: "kollegieUngRequirement", width: 18, group: "Kollegie- og Ungdomsboliger", type: "number" },
   { header: "", key: "separator7", width: 3, type: "separator" },
   { header: "Etageareal", key: "totalApplicationArea", width: 14, group: "I alt", type: "number" },
   { header: "Friarealskrav jf. Lokalplan", key: "totalRequirement", width: 18, group: "I alt", type: "number" },
   { header: "Overskud/Underskud af friareal", key: "freeAreaSurplus", width: 20, group: "I alt", type: "number" }
];

function normalizeReportReference(value: string) {
   return value.toLowerCase().replace(/[\s_-]+/g, "");
}

function toStringArray(value: any): string[] | undefined {
   if (typeof value === "string" && value.trim()) return [value.trim()];
   if (!Array.isArray(value)) return undefined;
   return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0).map((item) => item.trim());
}

function uniqueStrings(values: string[]) {
   return Array.from(new Set(values));
}

function configuredReportReference(value: any): Partial<ReportReference> | undefined {
   const directIds = toStringArray(value);
   if (directIds) return { ids: directIds };
   if (!value || typeof value !== "object") return undefined;
   return {
      ids: toStringArray(value.ids) || toStringArray(value.guids),
      names: toStringArray(value.names)
   };
}

function mergeReportReference(defaultReference: ReportReference, configured: Partial<ReportReference> | undefined): ReportReference {
   if (!configured) return defaultReference;
   return {
      ids: uniqueStrings([...(configured.ids || []), ...defaultReference.ids]),
      names: uniqueStrings([...(configured.names || []), ...(defaultReference.names || [])])
   };
}

function getFreeAreaReportReferences(project: PLAProject): FreeAreaReportReferences {
   const settings = project.settings?.freeAreaReport || project.settings?.friarealsopgorelseReport || {};
   const elementTypes = settings.elementTypes || settings.elementTypeIds || {};
   const attributes = settings.attributes || settings.attributeIds || {};
   return {
      elementTypes: {
         delomraade: mergeReportReference(defaultFreeAreaReportReferences.elementTypes.delomraade, configuredReportReference(elementTypes.delomraade)),
         byggefelt: mergeReportReference(defaultFreeAreaReportReferences.elementTypes.byggefelt, configuredReportReference(elementTypes.byggefelt)),
         anvendelse: mergeReportReference(defaultFreeAreaReportReferences.elementTypes.anvendelse, configuredReportReference(elementTypes.anvendelse))
      },
      attributes: {
         anvendelse: mergeReportReference(defaultFreeAreaReportReferences.attributes.anvendelse, configuredReportReference(attributes.anvendelse)),
         etageareal: mergeReportReference(defaultFreeAreaReportReferences.attributes.etageareal, configuredReportReference(attributes.etageareal)),
         friarealskrav: mergeReportReference(defaultFreeAreaReportReferences.attributes.friarealskrav, configuredReportReference(attributes.friarealskrav)),
         friareal: mergeReportReference(defaultFreeAreaReportReferences.attributes.friareal, configuredReportReference(attributes.friareal)),
         anvendelseKorrigeret: mergeReportReference(defaultFreeAreaReportReferences.attributes.anvendelseKorrigeret, configuredReportReference(attributes.anvendelseKorrigeret))
      }
   };
}

function matchesReportReference(value: string, references: string[]) {
   const normalizedValue = normalizeReportReference(value);
   return references.some((reference) => normalizeReportReference(reference) === normalizedValue);
}

function matchesElementTypeReference(project: PLAProject, element: PLAElementRecord, reference: ReportReference) {
   if (matchesReportReference(element.elementType, reference.ids)) return true;
   return reference.names ? matchesReportReference(elementTypeName(project, element), reference.names) : false;
}

function findReportAttributeId(project: PLAProject, element: PLAElementRecord, references: ReportReference | string[]): string | undefined {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const attributeIds = Array.from(new Set([...Object.keys(definitions), ...Object.keys(element.attributes || {})]));
   if (!Array.isArray(references)) {
      const idMatch = attributeIds.find((attributeId) => matchesReportReference(attributeId, references.ids));
      if (idMatch) return idMatch;
      const names = references.names;
      return names ? attributeIds.find((attributeId) => matchesReportReference(definitions[attributeId]?.name || "", names)) : undefined;
   }
   return attributeIds.find((attributeId) => matchesReportReference(attributeId, references) || matchesReportReference(definitions[attributeId]?.name || "", references));
}

function collectReportAttributeLookup(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]) {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const calculated = resolvePLACalculatedAttributes(element, project, elements, relations, { includeErrors: true });
   const warningsByAttributeId = getPLAAlertDetails(element, project, elements, relations).reduce<Record<string, string>>((acc, alert) => {
      acc[alert.attributeId] = acc[alert.attributeId] ? `${acc[alert.attributeId]}\n${alert.message}` : alert.message;
      return acc;
   }, {});

   return (references: ReportReference | string[]): { value: any; warning?: string } => {
      const attributeId = findReportAttributeId(project, element, references);
      if (!attributeId) return { value: "" };
      const definition = definitions[attributeId];
      const value = definition?.type === "expression" ? calculated[attributeId] : getConstantValue(element.attributes[attributeId]);
      return { value: formatValue(value), warning: warningsByAttributeId[attributeId] };
   };
}

function addGroupedNumericTotals(target: Record<string, number>, values: Record<string, any>, keys: string[]) {
   keys.forEach((key) => {
      const value = values[key];
      if (typeof value === "number" && Number.isFinite(value)) target[key] = (target[key] || 0) + value;
   });
}

function toFiniteNumber(value: any): number {
   if (typeof value === "number" && Number.isFinite(value)) return value;
   if (typeof value !== "string") return 0;
   const trimmed = value.trim().replace(/\s+/g, "");
   const normalized = trimmed.includes(",") ? trimmed.replace(/\./g, "").replace(",", ".") : trimmed;
   const parsed = Number(normalized);
   return Number.isFinite(parsed) ? parsed : 0;
}

function getReportTextValue(project: PLAProject, element: PLAElementRecord, elements: PLAElementRecord[], relations: PLARelationRecord[], references: ReportReference | string[]): string {
   const value = collectReportAttributeLookup(element, project, elements, relations)(references).value;
   return value === undefined || value === null ? "" : String(value).trim();
}

function getReportNumberValue(project: PLAProject, element: PLAElementRecord, elements: PLAElementRecord[], relations: PLARelationRecord[], references: ReportReference | string[]): number {
   return toFiniteNumber(collectReportAttributeLookup(element, project, elements, relations)(references).value);
}

function findCubeDimensionIndex(project: PLAProject, dimensions: string[], reference: string): number {
   return dimensions.findIndex((elementTypeId) =>
      normalizeReportReference(elementTypeId) === normalizeReportReference(reference) ||
      normalizeReportReference(project.elementTypes[elementTypeId]?.name || "") === normalizeReportReference(reference)
   );
}

function findCubeMemberIds(project: PLAProject, elements: PLAElementRecord[], dimensionId: string, references: string[]): Set<string> {
   const normalizedReferences = references.map(normalizeReportReference);
   return new Set(elements
      .filter((element) => element.elementType === dimensionId &&
         [element.id, element.name].some((value) => normalizedReferences.includes(normalizeReportReference(value || ""))))
      .map((element) => element.id));
}

export function getFreeAreaReportApplicationAreas(project: PLAProject, byggefelt: PLAElementRecord, elements: PLAElementRecord[], relations: PLARelationRecord[]): Record<string, number> {
   const definitions = project.elementTypes[byggefelt.elementType]?.attributes || {};
   const attributeId = findReportAttributeId(project, byggefelt, getFreeAreaReportReferences(project).attributes.anvendelseKorrigeret);
   if (!attributeId) return {};

   const definition = definitions[attributeId];
   const calculated = resolvePLACalculatedAttributes(byggefelt, project, elements, relations, { includeErrors: true });
   const rawValue = definition?.type === "expression"
      ? calculated[attributeId]
      : definition?.type === "cube"
         ? getConstantValue(byggefelt.attributes[attributeId])
         : undefined;
   if (!rawValue || typeof rawValue !== "object" || !rawValue.cells || typeof rawValue.cells !== "object") return {};
   const dimensions = Array.isArray(rawValue.dimensions)
      ? rawValue.dimensions as string[]
      : definition?.type === "cube"
         ? (definition.cubeDimensions || []).map((dimension) => dimension.elementTypeId)
         : [];
   if (dimensions.length === 0) throw new Error(`The ${definition?.name || attributeId} cube has no dimension definitions`);

   const phaseIndex = findCubeDimensionIndex(project, dimensions, "Fase");
   const ownerIndex = findCubeDimensionIndex(project, dimensions, "Ejer");
   const applicationTypeIndex = findCubeDimensionIndex(project, dimensions, "Anvendelsestype");
   if (phaseIndex < 0 || ownerIndex < 0 || applicationTypeIndex < 0) {
      throw new Error(`The ${definition?.name || attributeId} cube must contain Fase, Ejer and Anvendelsestype dimensions`);
   }

   const phaseDimensionId = dimensions[phaseIndex];
   const ownerDimensionId = dimensions[ownerIndex];
   const applicationTypeDimensionId = dimensions[applicationTypeIndex];
   const allocatedPhaseIds = findCubeMemberIds(project, elements, phaseDimensionId, ["Allokeret"]);
   const ownerIds = new Set(elements.filter((element) => element.elementType === ownerDimensionId).map((element) => element.id));
   if (allocatedPhaseIds.size === 0) throw new Error(`The Fase dimension has no Allokeret member`);

   return Object.fromEntries(freeAreaApplicationGroups.map((group) => {
      const applicationTypeIds = findCubeMemberIds(project, elements, applicationTypeDimensionId, group.values);
      let total = 0;
      Object.entries(rawValue.cells as Record<string, unknown>).forEach(([key, rawCellValue]) => {
         const coordinates = key.split(PLA_CUBE_KEY_SEPARATOR);
         if (coordinates.length !== dimensions.length ||
            coordinates.some((coordinate) => coordinate === PLA_CUBE_TOTAL_KEY) ||
            !allocatedPhaseIds.has(coordinates[phaseIndex]) ||
            !ownerIds.has(coordinates[ownerIndex]) ||
            !applicationTypeIds.has(coordinates[applicationTypeIndex])) return;
         total += toFiniteNumber(rawCellValue);
      });
      return [group.key, total];
   }));
}

function matchesFreeAreaDelomraade(project: PLAProject, element: PLAElementRecord, references: FreeAreaReportReferences) {
   return matchesElementTypeReference(project, element, references.elementTypes.delomraade);
}

function matchesFreeAreaByggefelt(project: PLAProject, element: PLAElementRecord, references: FreeAreaReportReferences) {
   return matchesElementTypeReference(project, element, references.elementTypes.byggefelt);
}

function matchesFreeAreaAnvendelse(project: PLAProject, element: PLAElementRecord, references: FreeAreaReportReferences) {
   return matchesElementTypeReference(project, element, references.elementTypes.anvendelse);
}

function findFreeAreaApplicationGroup(anvendelseValue: string): FreeAreaApplicationGroup | undefined {
   const normalizedValue = normalizeReportReference(anvendelseValue);
   return freeAreaApplicationGroups.find((group) => group.values.some((value) => normalizeReportReference(value) === normalizedValue));
}

function collectFreeAreaReportGroups(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): FreeAreaReportGroup[] {
   const reportReferences = getFreeAreaReportReferences(project);
   const elementsById = new Map(elements.map((element) => [element.id, element]));
   const hierarchicalRelations = getPLAHierarchicalRelations(project, relations);
   const outgoingRelations = hierarchicalRelations.reduce<Record<string, PLARelationRecord[]>>((acc, relation) => {
      acc[relation.fromId] = [...(acc[relation.fromId] || []), relation];
      return acc;
   }, {});
   const incomingIds = new Set(hierarchicalRelations.map((relation) => relation.toId));
   const visited = new Set<string>();
   const groups: FreeAreaReportGroup[] = [];
   const relationLabel = (relation: PLARelationRecord) => project.relationTypes[relation.relationType]?.name || relation.relationType;
   const sortedOutgoing = (elementId: string) => [...(outgoingRelations[elementId] || [])].sort((a, b) => comparePLAReportRelations(a, b, elementsById, relationLabel));

   const ensureGroup = (name: string, sortElement?: PLAElementRecord) => {
      const groupName = name || Localization.getText("Unconnected element");
      let group = groups.find((item) => item.name === groupName);
      if (!group) {
         group = { name: groupName, rows: [], sortElement };
         groups.push(group);
      } else if (!group.sortElement && sortElement) {
         group.sortElement = sortElement;
      }
      return group;
   };

   const collectByggefeltApplications = (byggefelt: PLAElementRecord) => {
      const applicationRequirements: Record<string, number> = {};
      const pending = sortedOutgoing(byggefelt.id).map((relation) => relation.toId);
      const seen = new Set<string>();
      while (pending.length > 0) {
         const elementId = pending.shift()!;
         if (seen.has(elementId)) continue;
         seen.add(elementId);
         const element = elementsById.get(elementId);
         if (!element) continue;
         if (matchesFreeAreaAnvendelse(project, element, reportReferences)) {
            const anvendelseValue = getReportTextValue(project, element, elements, relations, reportReferences.attributes.anvendelse);
            const applicationGroup = findFreeAreaApplicationGroup(anvendelseValue);
            if (applicationGroup) {
               applicationRequirements[applicationGroup.key] = (applicationRequirements[applicationGroup.key] || 0) + getReportNumberValue(project, element, elements, relations, reportReferences.attributes.friarealskrav);
            }
         }
         sortedOutgoing(element.id).forEach((relation) => pending.push(relation.toId));
      }
      return { applicationRequirements };
   };

   const createByggefeltRow = (byggefelt: PLAElementRecord): FreeAreaReportDataRow => {
      const { applicationRequirements } = collectByggefeltApplications(byggefelt);
      const applicationAreas = getFreeAreaReportApplicationAreas(project, byggefelt, elements, relations);
      const totalApplicationArea = freeAreaApplicationGroups.reduce((total, group) => total + (applicationAreas[group.key] || 0), 0);
      const totalRequirement = freeAreaApplicationGroups.reduce((total, group) => total + (applicationRequirements[group.key] || 0), 0);
      return {
         sortElement: byggefelt,
         byggefeltName: byggefelt.name || getReportTextValue(project, byggefelt, elements, relations, ["Navn", "Name"]) || elementTypeName(project, byggefelt),
         freeTerrainArea: getReportNumberValue(project, byggefelt, elements, relations, reportReferences.attributes.friareal),
         applicationAreas,
         applicationRequirements,
         totalApplicationArea,
         totalRequirement
      };
   };

   const addElement = (element: PLAElementRecord, level: number, path: Set<string>, currentDelomraade?: PLAElementRecord, fallbackGroup?: PLAElementRecord) => {
      if (visited.has(element.id) || path.has(element.id)) return;
      visited.add(element.id);
      const nextPath = new Set(path);
      nextPath.add(element.id);
      const isDelomraade = matchesFreeAreaDelomraade(project, element, reportReferences);
      const nextDelomraade = isDelomraade ? element : currentDelomraade;
      const nextFallbackGroup = fallbackGroup || (level === 1 ? element : undefined);
      const nextDelomraadeName = nextDelomraade ? (nextDelomraade.name || elementTypeName(project, nextDelomraade)) : undefined;
      const nextFallbackGroupName = nextFallbackGroup ? (nextFallbackGroup.name || elementTypeName(project, nextFallbackGroup)) : undefined;

      if (matchesFreeAreaByggefelt(project, element, reportReferences)) {
         ensureGroup(nextDelomraadeName || nextFallbackGroupName || Localization.getText("Unconnected element"), nextDelomraade || nextFallbackGroup).rows.push(createByggefeltRow(element));
      }

      sortedOutgoing(element.id).forEach((relation) => {
         const targetElement = elementsById.get(relation.toId);
         if (!targetElement) return;
         addElement(targetElement, level + 1, nextPath, nextDelomraade, nextFallbackGroup);
      });
   };

   const roots = elements.filter((element) => !incomingIds.has(element.id));
   (roots.length ? roots : elements).sort(comparePLAReportElements).forEach((element) => addElement(element, 1, new Set()));
   elements.filter((element) => !visited.has(element.id)).sort(comparePLAReportElements).forEach((element) => addElement(element, 1, new Set()));
   return groups.filter((group) => group.rows.length > 0).sort((a, b) => comparePLAElementSortingNumber(a.sortElement, b.sortElement) || compareReportText(a.name, b.name));
}

function matchesArealRefGroupElement(project: PLAProject, element: PLAElementRecord) {
   return matchesElementType(project, element, "anvendelse") || matchesElementType(project, element, "andele");
}

function getArealRefGroup(project: PLAProject, element: PLAElementRecord, elements: PLAElementRecord[], relations: PLARelationRecord[]): ArealRefGroup {
   const arealRef = collectReportAttributeLookup(element, project, elements, relations)(["ArealRef"]);
   const label = arealRef.value !== "" && arealRef.value !== undefined && arealRef.value !== null ? String(arealRef.value).trim() : element.name || elementTypeName(project, element);
   return { key: normalizeReportReference(label), label, sortElement: element };
}

function applyWarningCellStyle(cell: ExcelJS.Cell, warning: string) {
   cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF2CC" } };
   cell.note = `${Localization.getText("Warning")}: ${warning}`;
}

function applyTotalRowStyle(row: ExcelJS.Row, kind: "subtotal" | "grandTotal") {
   row.font = { bold: true };
   const fillColor = kind === "grandTotal" ? "FFD9EAD3" : "FFF2F2F2";
   row.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillColor } };
   });
}

function applyFreeAreaSectionRowStyle(row: ExcelJS.Row) {
   row.font = reportFonts.section;
   for (let columnNumber = 1; columnNumber <= freeAreaReportColumns.length; columnNumber++) {
      const cell = row.getCell(columnNumber);
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: freeAreaHeaderFill } };
      cell.border = thinBorder;
      cell.alignment = { vertical: "middle", wrapText: true };
   }
}

function applyFreeAreaDataRowStyle(row: ExcelJS.Row, index: number) {
   const fillColor = index % 2 === 0 ? "FFFFFFFF" : freeAreaAlternateRowFill;
   for (let columnNumber = 1; columnNumber <= freeAreaReportColumns.length; columnNumber++) row.getCell(columnNumber).fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillColor } };
}

function applyFreeAreaSubtotalRowStyle(row: ExcelJS.Row) {
   row.font = { ...reportFonts.total };
   for (let columnNumber = 1; columnNumber <= freeAreaReportColumns.length; columnNumber++) row.getCell(columnNumber).fill = { type: "pattern", pattern: "solid", fgColor: { argb: freeAreaSubtotalFill } };
}

function collectReportRows(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): ReportRow[] {
   const elementsById = new Map(elements.map((element) => [element.id, element]));
   const hierarchicalRelations = getPLAHierarchicalRelations(project, relations);
   const outgoingRelations = hierarchicalRelations.reduce<Record<string, PLARelationRecord[]>>((acc, relation) => {
      acc[relation.fromId] = [...(acc[relation.fromId] || []), relation];
      return acc;
   }, {});
   const incomingIds = new Set(hierarchicalRelations.map((relation) => relation.toId));
   const visited = new Set<string>();
   const rows: ReportRow[] = [{
      kind: "data",
      level: 0,
      structure: project.name,
      relation: "",
      elementType: Localization.getText("Project"),
      name: project.name,
      attributes: {
         [Localization.getText("Project Description")]: project.description || "",
         [Localization.getText("ESIndexName")]: project.esIndexName || ""
      },
      attributeValueTypes: {},
      warnings: {}
   }];
   const relationLabel = (relation: PLARelationRecord) => project.relationTypes[relation.relationType]?.name || relation.relationType;
   const sortedOutgoing = (elementId: string) => [...(outgoingRelations[elementId] || [])].sort((a, b) => comparePLAReportRelations(a, b, elementsById, relationLabel));
   const grandTotal: Record<string, number> = {};
   const totalValueTypes: Record<string, PLAValueType | undefined> = {};

   const addElement = (element: PLAElementRecord, level: number, relation: string, path: Set<string>): Record<string, number> => {
      const repeated = visited.has(element.id) || path.has(element.id);
      const { attributes, attributeValueTypes, warnings } = collectElementDetails(element, project, elements, relations);
      Object.entries(attributeValueTypes).forEach(([key, valueType]) => {
         if (totalValueTypes[key] === undefined) totalValueTypes[key] = valueType;
      });
      const subtreeTotal = getNumericTotals(attributes);
      rows.push({
         kind: "data",
         level,
         structure: element.name,
         relation,
         elementType: elementTypeName(project, element),
         name: element.name,
         attributes,
         attributeValueTypes,
         warnings
      });
      if (repeated) return subtreeTotal;
      visited.add(element.id);
      const nextPath = new Set(path);
      nextPath.add(element.id);
      let childCount = 0;
      sortedOutgoing(element.id).forEach((relation) => {
         const targetElement = elementsById.get(relation.toId);
         if (!targetElement) return;
         childCount += 1;
         addTotals(subtreeTotal, addElement(targetElement, level + 1, relationLabel(relation), nextPath));
      });
      if (childCount > 0) {
         rows.push({
            kind: "subtotal",
            level: level + 1,
            structure: `${Localization.getText("Sub-total")}: ${element.name}`,
            relation: "",
            elementType: "",
            name: "",
            attributes: subtreeTotal,
            attributeValueTypes: totalValueTypes,
            warnings: {}
         });
      }
      return subtreeTotal;
   };

   const roots = elements.filter((element) => !incomingIds.has(element.id));
   (roots.length ? roots : elements).sort(comparePLAReportElements).forEach((element) => addTotals(grandTotal, addElement(element, 1, Localization.getText("Project node"), new Set())));
   elements.filter((element) => !visited.has(element.id)).sort(comparePLAReportElements).forEach((element) => addTotals(grandTotal, addElement(element, 1, Localization.getText("Unconnected element"), new Set())));
   rows.push({
      kind: "grandTotal",
      level: 0,
      structure: Localization.getText("Grand total"),
      relation: "",
      elementType: "",
      name: "",
      attributes: grandTotal,
      attributeValueTypes: totalValueTypes,
      warnings: {}
   });
   return rows;
}

function collectGroupedReportRows(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): { rows: GroupedReportRow[]; arealRefGroups: ArealRefGroup[] } {
   const elementsById = new Map(elements.map((element) => [element.id, element]));
   const hierarchicalRelations = getPLAHierarchicalRelations(project, relations);
   const outgoingRelations = hierarchicalRelations.reduce<Record<string, PLARelationRecord[]>>((acc, relation) => {
      acc[relation.fromId] = [...(acc[relation.fromId] || []), relation];
      return acc;
   }, {});
   const incomingIds = new Set(hierarchicalRelations.map((relation) => relation.toId));
   const visited = new Set<string>();
   const bydelGroups: { name: string; rows: GroupedReportRow[]; totals: Record<string, number>; sortElement?: PLAElementRecord }[] = [];
   const arealRefGroupsByKey = new Map<string, ArealRefGroup>();
   const grandTotal: Record<string, number> = {};
   const relationLabel = (relation: PLARelationRecord) => project.relationTypes[relation.relationType]?.name || relation.relationType;
   const sortedOutgoing = (elementId: string) => [...(outgoingRelations[elementId] || [])].sort((a, b) => comparePLAReportRelations(a, b, elementsById, relationLabel));

   const ensureBydelGroup = (bydelElement: PLAElementRecord) => {
      const name = bydelElement.name || elementTypeName(project, bydelElement);
      let group = bydelGroups.find((item) => item.name === name);
      if (!group) {
         group = { name, rows: [], totals: {}, sortElement: bydelElement };
         bydelGroups.push(group);
      } else if (!group.sortElement) {
         group.sortElement = bydelElement;
      }
      return group;
   };

   const registerArealRefGroup = (group: ArealRefGroup) => {
      if (!group.key) return;
      if (!arealRefGroupsByKey.has(group.key)) arealRefGroupsByKey.set(group.key, group);
   };

   elements.filter((element) => matchesArealRefGroupElement(project, element)).forEach((element) => registerArealRefGroup(getArealRefGroup(project, element, elements, relations)));

   const collectAnvendelseValues = (byggefelt: PLAElementRecord, values: Record<string, any>, warnings: Record<string, string>) => {
      const pending = sortedOutgoing(byggefelt.id).map((relation) => relation.toId);
      const seen = new Set<string>();
      while (pending.length > 0) {
         const elementId = pending.shift()!;
         if (seen.has(elementId)) continue;
         seen.add(elementId);
         const element = elementsById.get(elementId);
         if (!element) continue;
         if (matchesArealRefGroupElement(project, element)) {
            const group = getArealRefGroup(project, element, elements, relations);
            registerArealRefGroup(group);
            const lookup = collectReportAttributeLookup(element, project, elements, relations);
            groupedReportAnvendelseAttributes.forEach((attributeName) => {
               const key = `arealRef:${group.key}:${attributeName}`;
               const detail = lookup([attributeName]);
               if (typeof detail.value === "number" && Number.isFinite(detail.value)) values[key] = (values[key] || 0) + detail.value;
               else if (values[key] === undefined) values[key] = detail.value;
               if (detail.warning) warnings[key] = warnings[key] ? `${warnings[key]}\n${detail.warning}` : detail.warning;
            });
         }
         sortedOutgoing(element.id).forEach((relation) => pending.push(relation.toId));
      }
   };

   const createByggefeltRow = (bydelElement: PLAElementRecord, byggefeltElement: PLAElementRecord): GroupedReportRow => {
      const values: Record<string, any> = { bydel: bydelElement.name || elementTypeName(project, bydelElement), byggefelt: byggefeltElement.name || elementTypeName(project, byggefeltElement) };
      const warnings: Record<string, string> = {};
      const lookup = collectReportAttributeLookup(byggefeltElement, project, elements, relations);
      groupedReportFixedAttributes.forEach((attributeName) => {
         const detail = lookup([attributeName]);
         values[attributeName] = detail.value;
         if (detail.warning) warnings[attributeName] = detail.warning;
      });
      collectAnvendelseValues(byggefeltElement, values, warnings);
      return { kind: "data", level: 1, values, warnings, sortElement: byggefeltElement };
   };

   const addElement = (element: PLAElementRecord, level: number, path: Set<string>, currentBydel?: PLAElementRecord) => {
      if (visited.has(element.id) || path.has(element.id)) return;
      visited.add(element.id);
      const isBydel = matchesElementType(project, element, "bydel") || (!currentBydel && level === 1);
      const isByggefelt = matchesElementType(project, element, "byggefelt");
      const bydelElement = isBydel ? element : currentBydel;
      const nextPath = new Set(path);
      nextPath.add(element.id);

      if (isByggefelt && bydelElement) {
         const group = ensureBydelGroup(bydelElement);
         const row = createByggefeltRow(bydelElement, element);
         group.rows.push(row);
         addGroupedNumericTotals(group.totals, row.values, groupedReportFixedAttributes);
      }

      sortedOutgoing(element.id).forEach((relation) => {
         const targetElement = elementsById.get(relation.toId);
         if (!targetElement) return;
         addElement(targetElement, level + 1, nextPath, bydelElement);
      });
   };

   const roots = elements.filter((element) => !incomingIds.has(element.id));
   (roots.length ? roots : elements).sort(comparePLAReportElements).forEach((element) => addElement(element, 1, new Set()));
   elements.filter((element) => !visited.has(element.id)).sort(comparePLAReportElements).forEach((element) => addElement(element, 1, new Set()));

   const arealRefGroups = Array.from(arealRefGroupsByKey.values()).sort((a, b) => comparePLAElementSortingNumber(a.sortElement, b.sortElement) || compareReportText(a.label, b.label));
   const totalKeys = [...groupedReportFixedAttributes, ...arealRefGroups.flatMap((group) => groupedReportAnvendelseAttributes.map((attributeName) => `arealRef:${group.key}:${attributeName}`))];
   const rows = bydelGroups.sort((a, b) => comparePLAElementSortingNumber(a.sortElement, b.sortElement) || compareReportText(a.name, b.name)).flatMap<GroupedReportRow>((group) => {
      const groupTotals: Record<string, number> = {};
      group.rows.sort((a, b) => comparePLAElementSortingNumber(a.sortElement, b.sortElement) || compareReportText(String(a.values.byggefelt || ""), String(b.values.byggefelt || "")));
      group.rows.forEach((row) => addGroupedNumericTotals(groupTotals, row.values, totalKeys));
      Object.assign(group.totals, groupTotals);
      const subtotalValues: Record<string, any> = { bydel: group.name, byggefelt: Localization.getText("Sub-total") };
      Object.entries(group.totals).forEach(([key, value]) => {
         subtotalValues[key] = value;
         grandTotal[key] = (grandTotal[key] || 0) + value;
      });
      return [...group.rows, { kind: "subtotal", level: 0, values: subtotalValues, warnings: {} }];
   });
   rows.push({ kind: "grandTotal", level: 0, values: { bydel: Localization.getText("Grand total"), byggefelt: "", ...grandTotal }, warnings: {} });
   return { rows, arealRefGroups };
}

function downloadBlob(fileName: string, blob: Blob) {
   const url = URL.createObjectURL(blob);
   const link = document.createElement("a");
   link.href = url;
   link.download = fileName;
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   URL.revokeObjectURL(url);
}

function addGroupedHeaderRows(worksheet: ExcelJS.Worksheet, title: string, columns: GroupedReportColumn[]) {
   worksheet.getRow(1).height = 38;
   worksheet.getCell(1, 3).value = title;
   worksheet.getCell(1, 3).font = reportFonts.title;
   worksheet.getCell(1, 3).alignment = { vertical: "middle", horizontal: "left" };

   const groupHeaderRow = worksheet.getRow(2);
   const columnHeaderRow = worksheet.getRow(3);
   groupHeaderRow.height = 22;
   columnHeaderRow.height = 30;

   for (let index = 0; index < columns.length; index++) {
      const columnNumber = index + 1;
      const column = columns[index];
      if (!column.group) {
         worksheet.mergeCells(2, columnNumber, 3, columnNumber);
         const cell = groupHeaderRow.getCell(columnNumber);
         cell.value = column.header;
         cell.font = reportFonts.header;
         cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
         cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
         cell.border = thinBorder;
         continue;
      }

      if (index === 0 || columns[index - 1].group !== column.group) {
         let endIndex = index;
         while (columns[endIndex + 1]?.group === column.group) endIndex += 1;
         worksheet.mergeCells(2, columnNumber, 2, endIndex + 1);
         const cell = groupHeaderRow.getCell(columnNumber);
         cell.value = column.group;
         cell.font = reportFonts.section;
         cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
         cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFB4C6E7" } };
         cell.border = thinBorder;
      }

      const subHeaderCell = columnHeaderRow.getCell(columnNumber);
      subHeaderCell.value = column.header;
      subHeaderCell.font = reportFonts.header;
      subHeaderCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      subHeaderCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
      subHeaderCell.border = thinBorder;
   }
}

function addFreeAreaHeaderRows(worksheet: ExcelJS.Worksheet, title: string, columns: FreeAreaReportColumn[]) {
   addGroupedHeaderRows(worksheet, title, columns);
   worksheet.getRow(1).height = 72;
   worksheet.getCell(1, 3).value = "";
   worksheet.mergeCells(1, 4, 1, 8);
   const titleCell = worksheet.getCell(1, 4);
   titleCell.value = title;
   titleCell.font = reportFonts.title;
   titleCell.alignment = { vertical: "middle", horizontal: "left" };

   for (let columnNumber = 1; columnNumber <= columns.length; columnNumber++) {
      [2, 3].forEach((rowNumber) => {
         const cell = worksheet.getCell(rowNumber, columnNumber);
         cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: freeAreaHeaderFill } };
         cell.font = reportFonts.header;
         cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
         cell.border = thinBorder;
      });
   }

   for (let index = 0; index < columns.length; index++) {
      const column = columns[index];
      if (!column.group || (index > 0 && columns[index - 1].group === column.group)) continue;
      let endIndex = index;
      while (columns[endIndex + 1]?.group === column.group) endIndex += 1;
      for (let columnNumber = index + 1; columnNumber <= endIndex + 1; columnNumber++) {
         const cell = worksheet.getCell(2, columnNumber);
         cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
         cell.font = reportFonts.section;
         cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: freeAreaHeaderFill } };
         cell.border = thinBorder;
      }
   }
}

async function addMolbakLogo(workbook: ExcelJS.Workbook, worksheet: ExcelJS.Worksheet, _columnCount: number, height = 32) {
   try {
      const buffer = await fetch("customerLogo/Molbak.png").then((response) => response.arrayBuffer());
      const logo = workbook.addImage({ buffer, extension: "png" });
      worksheet.addImage(logo, {
         tl: { col: 0, row: 0 },
         ext: { width: Math.round(height * 3.75), height }
      });
   } catch {
      // Logo is optional; the report should still download if the customer asset is unavailable.
   }
}

function styleGroupedReportWorksheet(worksheet: ExcelJS.Worksheet, columns: GroupedReportColumn[]) {
   worksheet.properties.defaultRowHeight = 18;
   worksheet.pageSetup = { orientation: "landscape", paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
   worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 3) row.font = reportFonts.body;
      row.eachCell((cell) => {
         cell.border = thinBorder;
         cell.alignment = { vertical: "middle", wrapText: true };
      });
   });
   columns.forEach((column, index) => {
      const worksheetColumn = worksheet.getColumn(index + 1);
      worksheetColumn.width = column.width;
      if (index >= 2) worksheetColumn.numFmt = "#,##0.####\" m2\"";
   });
}

function styleFreeAreaReportWorksheet(worksheet: ExcelJS.Worksheet, columns: FreeAreaReportColumn[]) {
   worksheet.properties.defaultRowHeight = 18;
   worksheet.pageSetup = { orientation: "landscape", paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
   worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 3) row.font = row.font || reportFonts.body;
      row.eachCell((cell) => {
         cell.border = thinBorder;
         cell.alignment = { vertical: "middle", wrapText: true };
      });
   });
   columns.forEach((column, index) => {
      const worksheetColumn = worksheet.getColumn(index + 1);
      worksheetColumn.width = column.width;
      if (column.type === "number") worksheetColumn.numFmt = "#,##0\" m2\"";
      if (column.type === "percent") worksheetColumn.numFmt = "0.0%";
      if (column.type === "separator") {
         worksheetColumn.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
      }
   });
   worksheet.eachRow((row) => styleFreeAreaSurplusCell(row.getCell("freeAreaSurplus")));
}

function getExcelColumnLetter(columnIndex: number) {
   let value = columnIndex;
   let name = "";
   while (value > 0) {
      const remainder = (value - 1) % 26;
      name = String.fromCharCode(65 + remainder) + name;
      value = Math.floor((value - 1) / 26);
   }
   return name;
}

function getFreeAreaColumnLetter(key: string) {
   return getExcelColumnLetter(freeAreaReportColumns.findIndex((column) => column.key === key) + 1);
}

function setFormula(cell: ExcelJS.Cell, formula: string, result: number | string) {
   cell.value = { formula, result };
}

function setFreeAreaPercentFormula(row: ExcelJS.Row, percentKey: string, areaKey: string, area: number, totalApplicationArea: number) {
   const rowNumber = row.number;
   const areaColumn = getFreeAreaColumnLetter(areaKey);
   setFormula(row.getCell(percentKey), `IF(AG${rowNumber}=0,"",${areaColumn}${rowNumber}/AG${rowNumber})`, totalApplicationArea > 0 ? area / totalApplicationArea : "");
}

function setSumFormula(row: ExcelJS.Row, key: string, startRow: number, endRow: number, result: number) {
   const column = getFreeAreaColumnLetter(key);
   setFormula(row.getCell(key), `SUM(${column}${startRow}:${column}${endRow})`, result);
}

function setSumRowsFormula(row: ExcelJS.Row, key: string, rowNumbers: number[], result: number) {
   const column = getFreeAreaColumnLetter(key);
   setFormula(row.getCell(key), rowNumbers.length ? `SUM(${rowNumbers.map((rowNumber) => `${column}${rowNumber}`).join(",")})` : "0", result);
}

function getCellNumericValue(cell: ExcelJS.Cell) {
   const value = cell.value;
   if (typeof value === "number") return value;
   if (value && typeof value === "object" && "result" in value && typeof value.result === "number") return value.result;
   return undefined;
}

function styleFreeAreaSurplusCell(cell: ExcelJS.Cell) {
   const value = getCellNumericValue(cell);
   if (value === undefined || value >= 0) return;
   cell.font = { ...cell.font, color: { argb: "FFFF0000" } };
}

function getFreeAreaGroupTotals(rows: FreeAreaReportDataRow[]) {
   return rows.reduce((totals, row) => {
      totals.freeTerrainArea += row.freeTerrainArea;
      totals.freeRoofArea += 0;
      totals.freeAreaTotal += row.freeTerrainArea;
      totals.totalApplicationArea += row.totalApplicationArea;
      totals.totalRequirement += row.totalRequirement;
      freeAreaApplicationGroups.forEach((group) => {
         totals.applicationAreas[group.key] = (totals.applicationAreas[group.key] || 0) + (row.applicationAreas[group.key] || 0);
         totals.applicationRequirements[group.key] = (totals.applicationRequirements[group.key] || 0) + (row.applicationRequirements[group.key] || 0);
      });
      return totals;
   }, {
      freeTerrainArea: 0,
      freeRoofArea: 0,
      freeAreaTotal: 0,
      totalApplicationArea: 0,
      totalRequirement: 0,
      applicationAreas: {} as Record<string, number>,
      applicationRequirements: {} as Record<string, number>
   });
}

function addFreeAreaDataRow(worksheet: ExcelJS.Worksheet, rowData: FreeAreaReportDataRow, rowIndex: number) {
   const excelRow = worksheet.addRow({
      byggefelt: rowData.byggefeltName,
      byggefeltStatus: "A",
      freeTerrainArea: rowData.freeTerrainArea,
      freeRoofArea: 0,
      boligAlmen: 0,
      serviceErhverv: 0,
      totalApplicationArea: rowData.totalApplicationArea,
      totalRequirement: rowData.totalRequirement
   });
   const rowNumber = excelRow.number;
   freeAreaApplicationGroups.forEach((group) => {
      excelRow.getCell(group.areaKey).value = rowData.applicationAreas[group.key] || 0;
      if (group.secondaryKey) excelRow.getCell(group.secondaryKey).value = 0;
      excelRow.getCell(group.requirementKey).value = rowData.applicationRequirements[group.key] || 0;
      setFreeAreaPercentFormula(excelRow, group.percentKey, group.areaKey, rowData.applicationAreas[group.key] || 0, rowData.totalApplicationArea);
   });
   setFormula(excelRow.getCell("freeAreaTotal"), `C${rowNumber}+D${rowNumber}`, rowData.freeTerrainArea);
   setFormula(excelRow.getCell("totalApplicationArea"), `SUM(G${rowNumber},L${rowNumber},Q${rowNumber},U${rowNumber},Y${rowNumber},AC${rowNumber})`, rowData.totalApplicationArea);
   setFormula(excelRow.getCell("totalRequirement"), `SUM(J${rowNumber},O${rowNumber},S${rowNumber},W${rowNumber},AA${rowNumber},AE${rowNumber})`, rowData.totalRequirement);
   setFormula(excelRow.getCell("freeAreaSurplus"), `E${rowNumber}-AH${rowNumber}`, rowData.freeTerrainArea - rowData.totalRequirement);
   applyFreeAreaDataRowStyle(excelRow, rowIndex);
   styleFreeAreaSurplusCell(excelRow.getCell("freeAreaSurplus"));
   return excelRow;
}

function addFreeAreaSubtotalRow(worksheet: ExcelJS.Worksheet, groupName: string, rows: FreeAreaReportDataRow[], startRow: number, endRow: number) {
   const totals = getFreeAreaGroupTotals(rows);
   const excelRow = worksheet.addRow({ byggefelt: `${Localization.getText("Sub-total")}: ${groupName}` });
   ["freeTerrainArea", "freeRoofArea"].forEach((key) => setSumFormula(excelRow, key, startRow, endRow, totals[key as "freeTerrainArea" | "freeRoofArea"]));
   freeAreaApplicationGroups.forEach((group) => {
      setSumFormula(excelRow, group.areaKey, startRow, endRow, totals.applicationAreas[group.key] || 0);
      if (group.secondaryKey) setSumFormula(excelRow, group.secondaryKey, startRow, endRow, 0);
      setFreeAreaPercentFormula(excelRow, group.percentKey, group.areaKey, totals.applicationAreas[group.key] || 0, totals.totalApplicationArea);
      setSumFormula(excelRow, group.requirementKey, startRow, endRow, totals.applicationRequirements[group.key] || 0);
   });
   const rowNumber = excelRow.number;
   setFormula(excelRow.getCell("freeAreaTotal"), `C${rowNumber}+D${rowNumber}`, totals.freeAreaTotal);
   setFormula(excelRow.getCell("totalApplicationArea"), `SUM(G${rowNumber},L${rowNumber},Q${rowNumber},U${rowNumber},Y${rowNumber},AC${rowNumber})`, totals.totalApplicationArea);
   setFormula(excelRow.getCell("totalRequirement"), `SUM(J${rowNumber},O${rowNumber},S${rowNumber},W${rowNumber},AA${rowNumber},AE${rowNumber})`, totals.totalRequirement);
   setFormula(excelRow.getCell("freeAreaSurplus"), `E${rowNumber}-AH${rowNumber}`, totals.freeAreaTotal - totals.totalRequirement);
   applyFreeAreaSubtotalRowStyle(excelRow);
   styleFreeAreaSurplusCell(excelRow.getCell("freeAreaSurplus"));
   return excelRow;
}

function addFreeAreaGrandTotalRow(worksheet: ExcelJS.Worksheet, subtotalRowNumbers: number[], groups: FreeAreaReportGroup[]) {
   const allRows = groups.flatMap((group) => group.rows);
   const totals = getFreeAreaGroupTotals(allRows);
   const excelRow = worksheet.addRow({ byggefelt: Localization.getText("Grand total") });
   ["freeTerrainArea", "freeRoofArea"].forEach((key) => setSumRowsFormula(excelRow, key, subtotalRowNumbers, totals[key as "freeTerrainArea" | "freeRoofArea"]));
   freeAreaApplicationGroups.forEach((group) => {
      setSumRowsFormula(excelRow, group.areaKey, subtotalRowNumbers, totals.applicationAreas[group.key] || 0);
      if (group.secondaryKey) setSumRowsFormula(excelRow, group.secondaryKey, subtotalRowNumbers, 0);
      setFreeAreaPercentFormula(excelRow, group.percentKey, group.areaKey, totals.applicationAreas[group.key] || 0, totals.totalApplicationArea);
      setSumRowsFormula(excelRow, group.requirementKey, subtotalRowNumbers, totals.applicationRequirements[group.key] || 0);
   });
   const rowNumber = excelRow.number;
   setFormula(excelRow.getCell("freeAreaTotal"), `C${rowNumber}+D${rowNumber}`, totals.freeAreaTotal);
   setFormula(excelRow.getCell("totalApplicationArea"), `SUM(G${rowNumber},L${rowNumber},Q${rowNumber},U${rowNumber},Y${rowNumber},AC${rowNumber})`, totals.totalApplicationArea);
   setFormula(excelRow.getCell("totalRequirement"), `SUM(J${rowNumber},O${rowNumber},S${rowNumber},W${rowNumber},AA${rowNumber},AE${rowNumber})`, totals.totalRequirement);
   setFormula(excelRow.getCell("freeAreaSurplus"), `E${rowNumber}-AH${rowNumber}`, totals.freeAreaTotal - totals.totalRequirement);
   applyTotalRowStyle(excelRow, "grandTotal");
   styleFreeAreaSurplusCell(excelRow.getCell("freeAreaSurplus"));
   return excelRow;
}

function getLocalizedReportDate() {
   return new Intl.DateTimeFormat(navigator.language || "da-DK").format(new Date());
}

export async function createPLAProjectRelationReport(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]) {
   const rows = collectReportRows(project, elements, relations);
   const attributeHeaders = Array.from(new Set(rows.flatMap((row) => Object.keys(row.attributes))));
   const attributeColumns: AttributeReportColumn[] = attributeHeaders.map((header, index) => ({
      header,
      attributeKey: `attr${index}`,
      valueType: Array.from(new Set(rows.map((row) => row.attributeValueTypes[header]).filter(Boolean))).length === 1
         ? rows.map((row) => row.attributeValueTypes[header]).find(Boolean)
         : undefined
   }));
   const workbook = new ExcelJS.Workbook();
   const worksheet = workbook.addWorksheet(Localization.getText("PLA relation report"));
   worksheet.columns = [
      { header: Localization.getText("Level"), key: "level", width: 7 },
      { header: Localization.getText("Structure"), key: "structure", width: 34 },
      { header: Localization.getText("Relation"), key: "relation", width: 24 },
      { header: Localization.getText("Element type"), key: "elementType", width: 22 },
      { header: Localization.getText("Name"), key: "name", width: 26 },
      ...attributeColumns.map((column) => ({ header: column.header, key: column.attributeKey, width: 18 }))
   ];
   worksheet.views = [{ state: "frozen", ySplit: 1 }];
   worksheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: worksheet.columns.length } };
   worksheet.getRow(1).font = { bold: true };

   rows.forEach((row) => {
      const excelRow = worksheet.addRow({
         level: row.level,
         structure: row.structure,
         relation: row.relation,
         elementType: row.elementType,
         name: row.name,
         ...attributeColumns.reduce<Record<string, any>>((acc, column) => {
            acc[column.attributeKey] = row.attributes[column.header] ?? "";
            return acc;
         }, {})
      });
      excelRow.getCell("structure").alignment = { indent: Math.min(row.level, 12) };
      attributeColumns.forEach((column) => {
         const numberFormat = getExcelNumberFormat(row.attributeValueTypes[column.header] || column.valueType);
         if (numberFormat) excelRow.getCell(column.attributeKey).numFmt = numberFormat;
         const warning = row.warnings[column.header];
         if (!warning) return;
         const valueCell = excelRow.getCell(column.attributeKey);
         valueCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF2CC" } };
         valueCell.note = `${Localization.getText("Warning")}: ${warning}`;
      });
      if (row.kind === "subtotal" || row.kind === "grandTotal") {
         excelRow.font = { bold: true };
         excelRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: row.kind === "grandTotal" ? "FFD9EAD3" : "FFF2F2F2" } };
      }
   });

   const buffer = await workbook.xlsx.writeBuffer();
   downloadBlob(`${safeFileName(project.name)}-PLA-report.xlsx`, new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
}

export async function createPLAProjectGroupedRelationReport(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]) {
   const { rows, arealRefGroups } = collectGroupedReportRows(project, elements, relations);
   const columns: GroupedReportColumn[] = [
      { header: Localization.getText("Bydel name"), key: "bydel", width: 24 },
      { header: Localization.getText("Byggefelt name"), key: "byggefelt", width: 24 },
      ...groupedReportFixedAttributes.map((attributeName) => ({ header: attributeName, key: attributeName, width: 16, warningKey: attributeName })),
      ...arealRefGroups.flatMap((group, index) => groupedReportAnvendelseAttributes.map((attributeName) => ({ header: attributeName, key: `arealRef${index}${attributeName}`, width: 16, warningKey: `arealRef:${group.key}:${attributeName}`, group: group.label })))
   ];
   const workbook = new ExcelJS.Workbook();
   const worksheet = workbook.addWorksheet(Localization.getText("PLA grouped relation report"));
   worksheet.columns = columns.map((column) => ({ key: column.key, width: column.width }));
   addGroupedHeaderRows(worksheet, `${Localization.getText("PLA grouped relation report")}: ${project.name}`, columns);
   worksheet.views = [{ state: "frozen", ySplit: 3 }];
   worksheet.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: worksheet.columns.length } };

   rows.forEach((row) => {
      const excelRow = worksheet.addRow(columns.reduce<Record<string, any>>((acc, column) => {
         acc[column.key] = row.values[column.warningKey || column.key] ?? row.values[column.key] ?? "";
         return acc;
      }, {}));
      excelRow.getCell("bydel").alignment = { indent: Math.min(row.level, 8) };
      columns.forEach((column) => {
         if (!column.warningKey) return;
         const warning = row.warnings[column.warningKey];
         if (warning) applyWarningCellStyle(excelRow.getCell(column.key), warning);
      });
      if (row.kind === "subtotal" || row.kind === "grandTotal") applyTotalRowStyle(excelRow, row.kind);
   });
   styleGroupedReportWorksheet(worksheet, columns);
   await addMolbakLogo(workbook, worksheet, columns.length);

   const signatureStartRow = worksheet.rowCount + 2;
   worksheet.getCell(signatureStartRow, 1).value = "Revisionsdato:";
   worksheet.getCell(signatureStartRow, 2).value = "XX.XX.XXXX";
   worksheet.getCell(signatureStartRow + 1, 1).value = "Landinspektør";
   worksheet.getCell(signatureStartRow + 1, 2).value = "XXXXXXXXX";
   [signatureStartRow, signatureStartRow + 1].forEach((rowNumber) => {
      const row = worksheet.getRow(rowNumber);
      row.font = reportFonts.body;
      row.getCell(1).font = reportFonts.header;
   });

   const buffer = await workbook.xlsx.writeBuffer();
   downloadBlob(`${safeFileName(project.name)}-PLA-grouped-report.xlsx`, new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
}

export async function createPLAFreeAreaReport(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], userName: string) {
   const groups = collectFreeAreaReportGroups(project, elements, relations);
   const workbook = new ExcelJS.Workbook();
   const worksheet = workbook.addWorksheet("Friarealsopgørelse");
   worksheet.columns = freeAreaReportColumns.map((column) => ({ key: column.key, width: column.width }));
   addFreeAreaHeaderRows(worksheet, `Friarealsopgørelse: ${project.name}`, freeAreaReportColumns);
   worksheet.views = [{ state: "frozen", ySplit: 3 }];
   worksheet.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: worksheet.columns.length } };

   const subtotalRowNumbers: number[] = [];
   groups.forEach((group) => {
      const sectionRow = worksheet.addRow({ byggefelt: group.name });
      worksheet.mergeCells(sectionRow.number, 1, sectionRow.number, freeAreaReportColumns.length);
      applyFreeAreaSectionRowStyle(sectionRow);
      const dataStartRow = worksheet.rowCount + 1;
      group.rows.sort((a, b) => comparePLAElementSortingNumber(a.sortElement, b.sortElement) || compareReportText(a.byggefeltName, b.byggefeltName)).forEach((row, index) => addFreeAreaDataRow(worksheet, row, index));
      const dataEndRow = dataStartRow + group.rows.length - 1;
      const subtotalRow = addFreeAreaSubtotalRow(worksheet, group.name, group.rows, dataStartRow, dataEndRow);
      subtotalRowNumbers.push(subtotalRow.number);
   });
   addFreeAreaGrandTotalRow(worksheet, subtotalRowNumbers, groups);
   styleFreeAreaReportWorksheet(worksheet, freeAreaReportColumns);
   await addMolbakLogo(workbook, worksheet, freeAreaReportColumns.length, 90);

   const footerStartRow = worksheet.rowCount + 2;
   worksheet.getCell(footerStartRow, 1).value = "Revisionsdato:";
   worksheet.getCell(footerStartRow, 2).value = "";
   worksheet.getCell(footerStartRow + 1, 1).value = "Landinspektør:";
   worksheet.getCell(footerStartRow + 1, 2).value = "Frederik Viskum";
   worksheet.getCell(footerStartRow + 3, 1).value = "Udtrukket:";
   worksheet.getCell(footerStartRow + 3, 2).value = getLocalizedReportDate();
   worksheet.getCell(footerStartRow + 4, 1).value = "Bruger:";
   worksheet.getCell(footerStartRow + 4, 2).value = userName;
   [footerStartRow, footerStartRow + 1, footerStartRow + 3, footerStartRow + 4].forEach((rowNumber) => {
      const row = worksheet.getRow(rowNumber);
      row.font = reportFonts.body;
      row.getCell(1).font = reportFonts.header;
   });

   const buffer = await workbook.xlsx.writeBuffer();
   downloadBlob(`${safeFileName(project.name)}-Friarealsopgørelse.xlsx`, new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
}
